import { api } from ".";

export type DraftRunStatus =
  | "collecting"
  | "queued"
  | "running"
  | "worker_returned"
  | "awaiting_input"
  | "completed"
  | "failed"
  | "dead"
  | "canceled";

export type DraftConfidence = "High" | "Medium" | "Low";

export type DraftRecStatus =
  | "pending"
  | "accepted"
  | "overridden"
  | "rejected"
  | "created"
  | "needs_input"
  | "superseded";

export type DraftComponentType =
  | "provider_decomposition"
  | "payer_consolidation"
  | "contract_term"
  | "base_rate_file"
  | "rate_calculation"
  | "carveout"
  | "case_rate_ruleset"
  | "inpatient_ruleset"
  | "package_ruleset"
  | "exploding_fee_schedule"
  | "mpdr_ruleset"
  | "lesser_of_charges"
  | "charge_based_pricing"
  | "ma_payer_link";

export type DraftOverrideCategory =
  | "agent_misread_source"
  | "agent_missed_document"
  | "wrong_policy_mapping"
  | "contract_ambiguous";

export type DraftDocKind =
  | "contract_pdf"
  | "rate_excel"
  | "amendment"
  | "carveout_exhibit"
  | "other";

export interface IDraftAttribution {
  id: string;
  documentId: string | null;
  locator: string | null;
  fileName: string | null;
  exactQuote: string;
  verified: boolean;
  verificationMethod: string | null;
  transform: string | null;
}

export interface IDraftOverride {
  category: DraftOverrideCategory;
  reason: string;
  finalValue: unknown | null;
  agentWasWrong: boolean;
  createdAt: string;
}

export interface IDraftRecommendation {
  id: string;
  componentType: DraftComponentType;
  proposedModeling: unknown;
  altModeling: unknown | null;
  confidence: DraftConfidence;
  status: DraftRecStatus;
  missingInputs: unknown | null;
  openQuestions: unknown | null;
  implementedAt: string | null;
  sortOrder: number;
  attributions: IDraftAttribution[];
  override: IDraftOverride | null;
}

export interface IDraftDocument {
  id: string;
  kind: DraftDocKind;
  fileName: string;
  contentType: string | null;
  fileSize: number | null;
  extractionStatus: "pending" | "extracting" | "ready" | "failed";
}

export interface IDraftRunSummary {
  id: string;
  accountId: string;
  name: string | null;
  status: DraftRunStatus;
  createdById: string;
  providerId: string | null;
  providerName: string | null;
  payerId: number | null;
  payerName: string | null;
  siteTier: DraftSiteTier | null;
  createdAt: string;
  updatedAt: string;
  documentCount: number;
  recommendationCount: number;
}

export interface IDraftRunDetail extends IDraftRunSummary {
  latestVersionId: string | null;
  documents: IDraftDocument[];
  recommendations: IDraftRecommendation[];
}

export interface IPresignedUpload {
  documentId: string;
  uploadUrl: string;
  s3Key: string;
}

export type DraftCompareStatus = "match" | "missing" | "differs" | "extra";

export interface IDraftCompareItem {
  recommendationId: string | null;
  componentType: string | null;
  liveKey: string | null;
  status: DraftCompareStatus;
}

export interface IDraftCompareResult {
  compareId: string;
  items: IDraftCompareItem[];
}

// Per-recommendation self-verify result: how one recommendation lines up with production.
export type DraftVerdict = "match" | "differs" | "present" | "missing" | "n_a";
export interface IDraftRecMatch {
  verdict: DraftVerdict;
  rows: Record<string, unknown>[];
}
// Keyed by recommendationId.
export type IDraftVerification = Record<string, IDraftRecMatch>;

export type DraftSiteTier = "non_facility" | "facility" | "both";

export interface ICreateDraftRunRequest {
  name?: string;
  providerId?: string;
  payerId?: number;
  siteTier?: DraftSiteTier;
  note?: string;
  idempotencyKey?: string;
}

export interface IPresignDocumentRequest {
  runId: string;
  fileName: string;
  contentType: string;
  kind?: DraftDocKind;
  fileSize?: number;
}

export interface IOverrideRequest {
  recommendationId: string;
  category: DraftOverrideCategory;
  reason: string;
  finalValue?: unknown;
  agentWasWrong?: boolean;
}

export interface IAcceptRequest {
  recommendationId: string;
  editedModeling?: Record<string, unknown>;
}

const contractModelDraftsQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    listContractModelDraftRuns: builder.query<IDraftRunSummary[], void>({
      query: () => ({ url: "/contract-model-drafts/runs" }),
      transformResponse: (r: { data: IDraftRunSummary[] }) => r.data,
      providesTags: ["ContractModelDrafts"],
    }),
    getContractModelDraftRun: builder.query<IDraftRunDetail, string>({
      query: (id) => ({ url: `/contract-model-drafts/runs/${id}` }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      providesTags: ["ContractModelDrafts"],
    }),
    createContractModelDraftRun: builder.mutation<
      IDraftRunDetail,
      ICreateDraftRunRequest
    >({
      query: (body) => ({
        url: "/contract-model-drafts/runs",
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    presignContractModelDraftDocument: builder.mutation<
      IPresignedUpload,
      IPresignDocumentRequest
    >({
      query: ({ runId, ...body }) => ({
        url: `/contract-model-drafts/runs/${runId}/documents`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IPresignedUpload }) => r.data,
      // Deliberately does NOT invalidate: this only reserves a row; the bytes aren't in S3 yet.
      // confirmContractModelDraftDocument invalidates once the upload is verified, so the
      // document list never shows a file that isn't really there.
    }),
    confirmContractModelDraftDocument: builder.mutation<
      IDraftRunDetail,
      { runId: string; documentId: string }
    >({
      query: ({ runId, documentId }) => ({
        url: `/contract-model-drafts/runs/${runId}/documents/${documentId}/confirm`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    runContractModelDraftGeneration: builder.mutation<IDraftRunDetail, string>({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/generate`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    acceptContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      IAcceptRequest
    >({
      query: ({ recommendationId, ...body }) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/accept`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    rejectContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      string
    >({
      query: (recommendationId) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/reject`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    setContractModelDraftRecommendationImplemented: builder.mutation<
      IDraftRunDetail,
      { runId: string; recommendationId: string; implemented: boolean }
    >({
      query: ({ recommendationId, implemented }) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/implemented`,
        method: "POST",
        body: { implemented },
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      // Ticking a checkbox is a one-field change, so flip it in the cache immediately (instant feedback)
      // and reconcile with the server's run when it returns — no full-run refetch, no invalidatesTags.
      onQueryStarted: async (
        { runId, recommendationId, implemented },
        { dispatch, queryFulfilled }
      ) => {
        const patch = dispatch(
          contractModelDraftsQueries.util.updateQueryData(
            "getContractModelDraftRun",
            runId,
            (draft) => {
              const rec = draft.recommendations.find(
                (r) => r.id === recommendationId
              );
              if (rec)
                rec.implementedAt = implemented
                  ? new Date().toISOString()
                  : null;
            }
          )
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            contractModelDraftsQueries.util.updateQueryData(
              "getContractModelDraftRun",
              runId,
              (draft) => Object.assign(draft, data)
            )
          );
        } catch {
          patch.undo();
        }
      },
    }),
    runContractModelDraftCompare: builder.mutation<
      IDraftCompareResult,
      { runId: string }
    >({
      query: ({ runId }) => ({
        url: `/contract-model-drafts/runs/${runId}/compare`,
        method: "POST",
        body: {},
      }),
      transformResponse: (r: { data: IDraftCompareResult }) => r.data,
    }),
    getContractModelDraftVerification: builder.query<
      IDraftVerification,
      string
    >({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/verification`,
      }),
      transformResponse: (r: { data: IDraftVerification }) => r.data,
    }),
    overrideContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      IOverrideRequest
    >({
      query: ({ recommendationId, ...body }) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/override`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
  }),
});

export const {
  useListContractModelDraftRunsQuery,
  useGetContractModelDraftRunQuery,
  useCreateContractModelDraftRunMutation,
  useConfirmContractModelDraftDocumentMutation,
  usePresignContractModelDraftDocumentMutation,
  useRunContractModelDraftGenerationMutation,
  useAcceptContractModelDraftRecommendationMutation,
  useRejectContractModelDraftRecommendationMutation,
  useSetContractModelDraftRecommendationImplementedMutation,
  useRunContractModelDraftCompareMutation,
  useGetContractModelDraftVerificationQuery,
  useLazyGetContractModelDraftVerificationQuery,
  useOverrideContractModelDraftRecommendationMutation,
} = contractModelDraftsQueries;
